import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeviceCardActionArea from './Mechanisms/DeviceCardActionArea';

const ENTER_HANDLED_BY_FOCUSED_ELEMENT_SELECTOR = 'button, [href], input, select, textarea, [role="button"], [role="menuitem"], [role="option"]';

const isEnterHandledByFocusedElement = (target: EventTarget | null): boolean =>
  target instanceof Element && Boolean(target.closest(ENTER_HANDLED_BY_FOCUSED_ELEMENT_SELECTOR));

interface DeviceCardWrapperProps {
  header: string;
  headerIcon?: React.ElementType;
  initialOpen?: boolean;
  onDelete?: () => void;
  children: React.ReactNode;
  mechanismType?: string; // For future color-coding
}

/**
 * Generic wrapper component for device configuration cards.
 *
 * Provides consistent card structure with:
 * - Expandable/collapsible header
 * - Icon display
 * - Standardized spacing and layout
 *
 * Used by all device cards (drives, motors, sensors, mechanisms) to eliminate
 * duplicate code and ensure UI consistency.
 *
 * @example
 * ```tsx
 * <DeviceCardWrapper
 *   header="Tank Drive"
 *   headerIcon={TankIcon}
 *   initialOpen={true}
 * >
 *   <ReferenceFrameControl {...props} />
 *   <PortControl {...props} />
 * </DeviceCardWrapper>
 * ```
 */
export const DeviceCardWrapper: React.FC<DeviceCardWrapperProps> = ({ header, headerIcon, initialOpen = false, onDelete, children }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(initialOpen);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleExpandClick = () => {
    setExpanded((prev) => !prev);
  };

  const handleConfirmDelete = () => {
    setDeleteDialogOpen(false);
    onDelete?.();
  };

  return (
    <>
      <Card sx={{ height: '100%', width: '100%' }}>
        <Stack direction="column" width="100%">
          <Box sx={{ position: 'relative' }}>
            <DeviceCardActionArea
              headerIcon={headerIcon}
              header={header}
              expanded={expanded}
              hasHeaderAction={Boolean(onDelete)}
              handleExpandClick={handleExpandClick}
            />
            {onDelete && (
              <Tooltip title={t('deviceCard.delete', { defaultValue: 'Delete' })}>
                <IconButton
                  aria-label={t('deviceCard.delete_hardware', { defaultValue: 'Delete hardware' })}
                  color="inherit"
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    setDeleteDialogOpen(true);
                  }}
                  sx={{
                    position: 'absolute',
                    right: 76,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    width: 32,
                    height: 32,
                    color: 'text.secondary',
                    opacity: 0.45,
                    transition: 'color 120ms ease-in-out, opacity 120ms ease-in-out',
                    '&:hover, &:focus-visible': {
                      color: 'error.main',
                      opacity: 1,
                    },
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <CardContent>
              <Stack direction="column" spacing={2}>
                {children}
              </Stack>
            </CardContent>
          </Collapse>
        </Stack>
      </Card>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-device-dialog-title"
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !isEnterHandledByFocusedElement(event.target)) {
            event.preventDefault();
            handleConfirmDelete();
          }
        }}
      >
        <DialogTitle id="delete-device-dialog-title">{t('deviceCard.delete_dialog_title', { defaultValue: 'Delete hardware?' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deviceCard.delete_dialog_body', { defaultValue: 'This will remove {{hardwareName}} from your code.', hardwareName: header })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            {t('deviceCard.delete', { defaultValue: 'Delete' })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeviceCardWrapper;
